import { cn } from '@dialogueconsulting/sked-ui/lib/utils'

import { SmallApprovedIcon, TileRejectedIcon as SmallRejectedIcon } from 'Icons'
import { Button } from 'ui/button'

import ApprovalAnimation from './ApprovalAnimation'
import ReplyForm from './conversation/ReplyForm'
import type { Collaborator } from './conversation/types'

export interface PostActionProps {
  postId: string
  isLoading: boolean
  postStatus?: string
  isMobile?: boolean
  localPostStatus?: string
  reviewKey: string
  className?: string
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
  // Reply functionality props
  canReply?: boolean
  isReplied?: boolean
  isSubmitting?: boolean
  onReplySubmit?: (text: string, postId: string, commentId: string) => void
  lastMessageId?: string
  isConversationsEnabled?: boolean
  collaborators?: Collaborator[]
  // Approval animation props
  showApprovalAnimation?: boolean
  onApprovalAnimationComplete?: () => void
  // Error state
  actionError?: string | null
}

const PostAction = (props: PostActionProps) => {
  const {
    postId,
    isLoading,
    postStatus,
    isMobile,
    localPostStatus,
    reviewKey,
    updatePostState,
    className,
    // Reply functionality props
    canReply,
    isReplied,
    isSubmitting: replySubmitting,
    onReplySubmit,
    lastMessageId,
    // Approval animation props
    showApprovalAnimation,
    onApprovalAnimationComplete,
    // Error state
    actionError,
    collaborators,
  } = props
  if (!postStatus) return null

  const handleUndo = () => {
    updatePostState(reviewKey, postId, 'pending')
  }

  const handleApproved = () => {
    updatePostState(reviewKey, postId, 'approved')
  }

  const handleRequestChange = () => {
    updatePostState(reviewKey, postId, 'requestChange')
  }

  const isApproved = postStatus === 'approved'
  const isRequestChange = postStatus === 'requestChange'

  const showReplyForm =
    isRequestChange &&
    canReply &&
    !isReplied &&
    onReplySubmit &&
    Boolean(lastMessageId)

  return (
    <div className={className}>
      {!isMobile && (
        <h4 className="mb-[8px] font-gotham text-xl font-extrabold text-neutral-800">
          Post History & Discussion
        </h4>
      )}

      <div
        className={`${isLoading ? 'cursor-wait opacity-70' : ''} font-[Inter] w-full  lg:max-w-[360px]`}
      >
        {showApprovalAnimation && onApprovalAnimationComplete && (
          <ApprovalAnimation
            onComplete={onApprovalAnimationComplete}
            onUndo={handleUndo}
          />
        )}

        {(isApproved || isRequestChange) && !showApprovalAnimation && (
          // SKE-3808: resolved state uses the style-guide Notification design
          // (schedugram-client react-src/components/ui/notification.tsx):
          // tinted bg-*-50 + left accent bar + fa-regular icon + neutral-800
          // title. Success (green) for approved, error (red) for change.
          <div
            role="alert"
            className={cn(
              'relative flex w-full items-center gap-3 rounded-lg p-2 lg:max-w-[360px]',
              showReplyForm && 'rounded-b-none',
              isApproved ? 'bg-green-50' : 'bg-red-50'
            )}
          >
            <div
              className={cn(
                'w-0.5 shrink-0 self-stretch rounded-[3px]',
                isApproved ? 'bg-green-600' : 'bg-red-500'
              )}
              aria-hidden="true"
            />
            <div className="flex min-w-0 flex-1 items-center gap-2 py-1">
              <i
                className={cn(
                  'fa-regular flex size-5 shrink-0 items-center justify-center self-start text-base leading-none',
                  isApproved
                    ? 'fa-circle-check text-green-600'
                    : 'fa-circle-xmark text-red-500'
                )}
                aria-hidden="true"
              />
              <p className="m-0 flex-1 font-inter text-sm font-medium leading-5 text-neutral-800">
                {isApproved ? 'Approved!' : 'Change requested!'}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mr-2 shrink-0 self-center text-[12px] font-[500]"
              onClick={handleUndo}
            >
              Undo
            </Button>
          </div>
        )}

        {showReplyForm && (
          <ReplyForm
            postId={postId}
            isSubmitting={replySubmitting || false}
            isMobile={isMobile}
            onSubmit={(text, commentId) =>
              onReplySubmit(text, postId, commentId)
            }
            lastMessageId={lastMessageId}
            autoFocus={!isMobile}
            collaborators={collaborators ?? []}
          />
        )}
      </div>

      {isMobile && localPostStatus !== 'pending' && (
        <div className="mb-[10px]">
          <hr className="border-0 border-b border-[#dedafe] mx-auto my-[12px] mb-[16px]" />
          {postStatus === 'approved' && (
            <div>
              <div className="flex items-center justify-center">
                <div className="w-[28px] mr-[10px]">
                  <SmallApprovedIcon />
                </div>
                Post approved
              </div>
              <p className="text-center text-[12px] mt-[10px] mb-0">
                To undo this action, visit this link on a desktop device
              </p>
            </div>
          )}
          {postStatus === 'requestChange' && (
            <div>
              <div className="flex items-center justify-center">
                <div className="w-[28px] mr-[10px]">
                  <SmallRejectedIcon />
                </div>
                <div>Change requested</div>
              </div>
              <p className="text-center text-[12px] mt-[10px] mb-0">
                To undo this action, visit this link on a desktop device
              </p>
            </div>
          )}
        </div>
      )}

      <div
        className={`${isMobile && !(localPostStatus !== 'pending' && !isLoading) ? 'mb-[12px]' : ''}`}
      >
        {actionError && (
          <div
            className="bg-[#fee5e1] text-[#dc2626] text-[14px] font-[500] px-[12px] py-[8px] rounded-[8px] mb-[12px] w-full lg:max-w-[360px]"
            role="alert"
          >
            {actionError}
          </div>
        )}

        {postStatus === 'pending' && (
          <div
            className={`
              h-[48px] w-full lg:max-w-[360px] flex
              ${isMobile ? 'gap-[24px] justify-center' : 'gap-[16px]'}
              ${isLoading ? 'cursor-wait' : ''}
            `}
          >
            {/* kit Buttons — approve = green-primary, request change =
                red-primary (co-equal primary actions). Kept full-width and
                48px tall to match the resolved-state banner's rhythm.
                disabled={isLoading} blocks keyboard activation + announces
                the state to AT; the kit's disabled:pointer-events-none lets
                the wrapper's cursor-wait show through, and its disabled
                styles (opacity + bg-*-200) provide the dim. */}
            <Button
              data-testid="approve"
              variant="green-primary"
              disabled={isLoading}
              onClick={handleApproved}
              className="h-full flex-1 rounded-[8px] text-[16px]"
            >
              Approve
            </Button>

            <Button
              data-testid="requestChange"
              variant="red-primary"
              disabled={isLoading}
              onClick={handleRequestChange}
              className="h-full flex-1 rounded-[8px] text-[16px]"
            >
              Request change
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PostAction
